import { useEffect, useMemo, useState } from "react";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import {
  buildSuperAdminLoginActivityExportUrl,
  fetchSuperAdminLoginActivity,
} from "../api/superadmin";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import "./ReportsSectionCompact.css";

const PAGE_SIZES = [25, 50, 100, 250];

function formatBrowserDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatLocalDateTime(value) {
  if (!value) return "--";
  const text = String(value);
  const match = text.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (match) return `${match[1]} ${match[2]}`;
  return text;
}

function defaultFilters() {
  const today = new Date();
  const end = formatBrowserDate(today);
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 6);
  return {
    start_date: formatBrowserDate(startDate),
    end_date: end,
    platform: "",
    event_type: "",
    session_context: "",
    outcome: "",
    role: "",
    user_id: "",
    business_id: "",
    salon_id: "",
    ip_address: "",
    device_type: "",
    browser: "",
    os: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
    search: "",
    page: 1,
    page_size: 100,
  };
}

function optionLabel(option) {
  return option?.label || option?.name || option?.value || "";
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value ?? 0}</div>
    </div>
  );
}

export default function SuperAdminLoginActivity() {
  const { is_super_admin } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalPages = data?.total_pages || 1;
  const items = data?.items || [];
  const options = data?.options || {};
  const queryFilters = useMemo(() => filters, [filters]);

  const loadData = async (nextFilters = queryFilters) => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSuperAdminLoginActivity(nextFilters);
      setData(payload);
    } catch (err) {
      setError(err?.message || "Unable to load login activity.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (is_super_admin) {
      loadData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_super_admin]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === "page" ? value : 1,
    }));
  };

  const applyQuickFilter = (key) => {
    const next = { ...filters, page: 1 };
    if (key === "today") {
      const today = formatBrowserDate(new Date());
      next.start_date = today;
      next.end_date = today;
    } else if (key === "success") {
      next.event_type = "login_success";
      next.outcome = "success";
    } else if (key === "failed") {
      next.event_type = "login_failed";
    } else if (key === "myboothshare") {
      next.platform = "Ellyanna Boothshare";
    } else if (key === "mobile") {
      next.device_type = "mobile";
    } else if (key === "mfa") {
      next.event_type = "mfa_required";
    }
    setFilters(next);
    loadData(next);
  };

  const clearFilters = () => {
    const next = defaultFilters();
    setFilters(next);
    loadData(next);
  };

  const runSearch = () => loadData(filters);

  const goToPage = (page) => {
    const safePage = Math.max(1, Math.min(Number(page || 1), totalPages));
    const next = { ...filters, page: safePage };
    setFilters(next);
    loadData(next);
  };

  const exportCsv = () => {
    window.open(buildSuperAdminLoginActivityExportUrl(filters), "_blank", "noopener,noreferrer");
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
          <ReportPageHeader title="Login Activity" closeTo="/superadmin" />
          <div className={`space-y-6 px-6 py-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Login Activity"
              subtitle="Review recent sign-ins and access attempts across Ellyanna Booking and Ellyanna Boothshare."
              sections={[
                {
                  id: "filters",
                  title: "Filters",
                  description:
                    "Use date, platform, role, user ID, IP address, device, and IP location filters to narrow login activity.",
                  anchorId: "sa-login-filters",
                  bullets: [
                    "Dates and times use the login event salon or business timezone.",
                    "Location fields are derived from the login IP address when available.",
                    "Search works across name, email, IDs, professional ID, IP address, and location.",
                    "Export CSV downloads the current filtered activity list.",
                  ],
                },
              ]}
              troubleshooting={[
                {
                  title: "No activity showing",
                  body: "Widen the date range or clear filters. Login tracking starts after this audit feature is deployed.",
                },
              ]}
            />

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SummaryCard label="Total events" value={data?.summary?.total_events} />
              <SummaryCard label="Successful logins" value={data?.summary?.successful_logins} />
              <SummaryCard label="Failed logins" value={data?.summary?.failed_logins} />
              <SummaryCard label="Blocked" value={data?.summary?.blocked_logins} />
              <SummaryCard label="Unique users" value={data?.summary?.unique_users} />
            </div>

            <div id="sa-login-filters" className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-4 flex flex-wrap gap-2">
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("today")}>Today</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("success")}>Successful logins</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("failed")}>Failed logins</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("myboothshare")}>Ellyanna Boothshare</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("mobile")}>Mobile</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("mfa")}>MFA required</button>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className={LABEL_TEXT}>Start date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={filters.start_date} onChange={(event) => updateFilter("start_date", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>End date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={filters.end_date} onChange={(event) => updateFilter("end_date", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Platform</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.platform} onChange={(event) => updateFilter("platform", event.target.value)}>
                    <option value="">All platforms</option>
                    {(options.platforms || []).map((option) => <option key={option.value} value={option.value}>{optionLabel(option)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Event</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.event_type} onChange={(event) => updateFilter("event_type", event.target.value)}>
                    <option value="">All events</option>
                    {(options.event_types || []).map((option) => <option key={option.value} value={option.value}>{optionLabel(option)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Context</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.session_context} onChange={(event) => updateFilter("session_context", event.target.value)}>
                    <option value="">All contexts</option>
                    {(options.session_contexts || []).map((option) => <option key={option.value} value={option.value}>{optionLabel(option)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Outcome</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.outcome} onChange={(event) => updateFilter("outcome", event.target.value)}>
                    <option value="">All outcomes</option>
                    {(options.outcomes || []).map((option) => <option key={option.value} value={option.value}>{optionLabel(option)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Role</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.role} onChange={(event) => updateFilter("role", event.target.value)}>
                    <option value="">All roles</option>
                    {(options.roles || []).map((option) => <option key={option.value} value={option.value}>{optionLabel(option)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Device</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.device_type} onChange={(event) => updateFilter("device_type", event.target.value)}>
                    <option value="">All devices</option>
                    {(options.device_types || []).map((option) => <option key={option.value} value={option.value}>{optionLabel(option)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>User ID</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.user_id} onChange={(event) => updateFilter("user_id", event.target.value)} placeholder="User ID" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>IP Address</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.ip_address} onChange={(event) => updateFilter("ip_address", event.target.value)} placeholder="IP address" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Business</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.business_id} onChange={(event) => updateFilter("business_id", event.target.value)}>
                    <option value="">All businesses</option>
                    {(options.businesses || []).map((business) => <option key={business.id} value={business.id}>{business.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Salon</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.salon_id} onChange={(event) => updateFilter("salon_id", event.target.value)}>
                    <option value="">All salons</option>
                    {(options.salons || []).map((salon) => <option key={salon.id} value={salon.id}>{salon.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>City</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.city} onChange={(event) => updateFilter("city", event.target.value)} placeholder="IP city" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Province / State</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.province} onChange={(event) => updateFilter("province", event.target.value)} placeholder="IP province or state" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Country</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.country} onChange={(event) => updateFilter("country", event.target.value)} placeholder="Country" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Postal / ZIP</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.postal_code} onChange={(event) => updateFilter("postal_code", event.target.value)} placeholder="Postal or ZIP" />
                </div>
                <div className="xl:col-span-2">
                  <label className={LABEL_TEXT}>Search</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.search} onChange={(event) => updateFilter("search", event.target.value)} placeholder="Name, email, IDs, professional ID, IP, location" />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-3">
                <button type="button" className={BTN_OUTLINE} onClick={clearFilters}>Clear</button>
                <button type="button" className={BTN_OUTLINE} onClick={exportCsv}>Export CSV</button>
                <button type="button" className={BTN_PRIMARY} onClick={runSearch} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    {["Date", "Platform", "Event", "User", "Role", "Outcome", "Business", "Salon", "IP", "Device", "Location"].map((header) => (
                      <th key={header} className="px-4 py-3 text-left font-semibold">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={11} className="px-4 py-6 text-center text-slate-500">Loading login activity...</td></tr>
                  ) : items.length ? (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 text-slate-700">
                          <div>{formatLocalDateTime(row.occurred_at_local)}</div>
                          <div className="text-xs text-slate-500">{row.timezone || "--"}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{row.platform || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="font-semibold text-slate-900">{row.event_label || "--"}</div>
                          <div className="text-xs text-slate-500">{row.session_context_label || "--"}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="font-semibold text-slate-900">{row.display_name || "--"}</div>
                          <div className="text-xs text-slate-500">{row.email || "--"}{row.user_id ? ` · ID ${row.user_id}` : ""}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{row.role_label || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <div>{row.outcome || "--"}</div>
                          {row.failure_reason_code ? <div className="text-xs text-rose-600">{row.failure_reason_code}</div> : null}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{row.business_name || "--"}{row.business_id ? ` (ID ${row.business_id})` : ""}</td>
                        <td className="px-4 py-3 text-slate-700">{row.salon_name || "--"}{row.salon_id ? ` (ID ${row.salon_id})` : ""}</td>
                        <td className="px-4 py-3 text-slate-700">{row.ip_address || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">{[row.device_type, row.browser, row.os].filter(Boolean).join(" / ") || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <div>{[row.city, row.province, row.country].filter(Boolean).join(", ") || "--"}</div>
                          {row.ip_isp ? <div className="text-xs text-slate-500">{row.ip_isp}</div> : null}
                          {row.ip_geo_error ? <div className="text-xs text-amber-600">{row.ip_geo_error}</div> : null}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={11} className="px-4 py-6 text-center text-slate-500">No login activity found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                Showing {items.length ? (filters.page - 1) * filters.page_size + 1 : 0}
                {"-"}
                {Math.min(filters.page * filters.page_size, data?.total || 0)} of {data?.total || 0} events
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select className={FIELD_CLASS} value={filters.page_size} onChange={(event) => {
                  const next = { ...filters, page: 1, page_size: Number(event.target.value) };
                  setFilters(next);
                  loadData(next);
                }}>
                  {PAGE_SIZES.map((size) => <option key={size} value={size}>{size} per page</option>)}
                </select>
                <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(1)} disabled={filters.page <= 1}>First</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page - 1)} disabled={filters.page <= 1}>Previous</button>
                <span className="text-sm font-semibold text-slate-700">Page {filters.page} of {totalPages}</span>
                <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page + 1)} disabled={filters.page >= totalPages}>Next</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(totalPages)} disabled={filters.page >= totalPages}>Last</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
